export const styles = {
  // Main Container Styles
  container: {
    p: { xs: 2, sm: 3 },
    maxWidth: '100vw',
    overflow: 'hidden',
  },
  
  title: {
    fontWeight: 600,
    mb: 1,
    color: 'text.primary',
  },
  
  subtitle: {
    mb: 3,
    color: 'text.secondary',
  },
  
  // Simple Controls Section
  controls: {
    display: 'flex',
    gap: 2,
    mb: 3,
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  
  // Simple Chart Container
  chartCard: {
    minHeight: { xs: '70vh', sm: '75vh', md: '80vh' },
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  
  chartContent: {
    p: { xs: 1, sm: 2 },
    width: '100%',
    height: '100%',
    overflow: 'auto',
    flex: 1,
    '&::-webkit-scrollbar': {
      width: 6,
      height: 6,
    },
    '&::-webkit-scrollbar-track': {
      bgcolor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.600' : 'grey.400',
      borderRadius: 3,
      '&:hover': {
        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.500' : 'grey.600',
      },
    },
  },
  
  chartInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    minHeight: 'min-content',
    pb: { xs: 2, sm: 3 },
    position: 'relative',
    zIndex: 1,
  },
  
  // Separate sections for better organization
  companiesSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    gap: { xs: 3, sm: 4, md: 5 },
    mb: { xs: 2, sm: 3, md: 4 },
  },
  
  unassignedSection: {
    width: '100%',
    pt: { xs: 3, sm: 4, md: 6 },
    borderTop: '2px dashed',
    borderColor: (theme) => theme.palette.mode === 'dark' 
      ? theme.palette.grey[700] 
      : theme.palette.grey[400],
    position: 'relative',
    '&::before': {
      content: '"• • • • • • • • • • • • •"',
      position: 'absolute',
      top: -1,
      left: '50%',
      transform: 'translateX(-50%)',
      bgcolor: 'background.paper',
      px: 3,
      color: 'text.secondary',
      fontSize: '0.7rem',
      letterSpacing: '0.5em',
      opacity: 0.6,
    },
  },
  
  // Node Styles - Enhanced with dark mode support
  getNodeStyles: (nodeType, color) => {
    const baseStyles = {
      width: '100%',
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: { xs: 1.5, sm: 2 },
      p: { xs: 1, sm: 1.5, md: 2 },
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      backdropFilter: 'blur(10px)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: (theme) => theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))'
          : 'linear-gradient(135deg, rgba(255,255,255,0.8), rgba(255,255,255,0.4))',
        borderRadius: 'inherit',
        pointerEvents: 'none',
        zIndex: -1,
      },
    };

    switch (nodeType) {
      case 'company':
        return {
          ...baseStyles,
          minWidth: { xs: 240, sm: 280, md: 320 },
          maxWidth: { xs: 260, sm: 300, md: 340 },
          bgcolor: `${color}08`,
          borderColor: color,
          borderWidth: 2,
          elevation: 4,
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: 8,
            borderColor: color,
          },
        };
      case 'group':
        return {
          ...baseStyles,
          minWidth: { xs: 200, sm: 240, md: 280 },
          maxWidth: { xs: 220, sm: 260, md: 300 },
          bgcolor: `${color}06`,
          borderColor: color,
          borderWidth: 1.5,
          elevation: 2,
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: 6,
            borderColor: `${color}80`,
          },
        };
      case 'team':
        return {
          ...baseStyles,
          minWidth: { xs: 160, sm: 200, md: 240 },
          maxWidth: { xs: 180, sm: 220, md: 260 },
          bgcolor: `${color}04`,
          borderColor: color,
          borderWidth: 1,
          elevation: 2,
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: 6,
            borderColor: `${color}80`,
          },
        };
      case 'member':
        return {
          ...baseStyles,
          minWidth: { xs: 140, sm: 160, md: 180 },
          maxWidth: { xs: 150, sm: 170, md: 190 },
          elevation: 1,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4,
            borderColor: 'primary.main',
          },
        };
      default:
        return baseStyles;
    }
  },
  
  nodeContent: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 0.5, sm: 1, md: 1.5 },
  },
  
  expandButton: (isExpanded, color) => ({
    p: { xs: 0.25, sm: 0.5 },
    bgcolor: isExpanded ? color : 'grey.200',
    color: isExpanded ? 'white' : 'grey.600',
    minWidth: { xs: 20, sm: 24, md: 28 },
    height: { xs: 20, sm: 24, md: 28 },
    '&:hover': {
      bgcolor: isExpanded ? `${color}CC` : 'grey.300',
      transform: 'scale(1.1)',
    },
  }),
  
  nodeIcon: (color) => ({
    width: { xs: 28, sm: 32, md: 36 },
    height: { xs: 28, sm: 32, md: 36 },
    borderRadius: 1.5,
    bgcolor: `${color}15`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mr: { xs: 0.5, sm: 1, md: 1.5 },
    flexShrink: 0,
    border: `1px solid ${color}30`,
  }),
  
  nodeText: {
    flex: 1,
    minWidth: 0,
    width: { xs: '100%', sm: 'auto' },
  },
  
  nodeTitle: (nodeType, color) => ({
    lineHeight: 1.2,
    fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
    color: nodeType === 'company' ? color : 'text.primary',
    fontWeight: nodeType === 'company' ? 700 : 600,
  }),
  
  nodeSubtitle: {
    display: 'block',
    lineHeight: 1.2,
    mt: 0.5,
    fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
  },
  
  // Company Item Styles - Independent companies layout
  companyContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    padding: { xs: 2, sm: 3, md: 4 },
    bgcolor: (theme) => theme.palette.mode === 'dark' 
      ? 'rgba(255,255,255,0.02)' 
      : 'rgba(0,0,0,0.01)',
    borderRadius: { xs: 2, sm: 3 },
    border: '1px solid',
    borderColor: (theme) => theme.palette.mode === 'dark' 
      ? 'rgba(255,255,255,0.05)' 
      : 'rgba(0,0,0,0.05)',
    backdropFilter: 'blur(10px)',
    '&:hover': {
      bgcolor: (theme) => theme.palette.mode === 'dark' 
        ? 'rgba(255,255,255,0.03)' 
        : 'rgba(0,0,0,0.02)',
      borderColor: (theme) => theme.palette.mode === 'dark' 
        ? 'rgba(255,255,255,0.1)' 
        : 'rgba(0,0,0,0.08)',
    },
  },
  
  companyNode: {
    display: 'flex',
    justifyContent: 'center',
    mb: { xs: 2, sm: 3, md: 4 },
    px: { xs: 1, sm: 0 },
  },
  
  companyExpanded: {
    position: 'relative',
    px: { xs: 0, sm: 1, md: 0 },
  },
  
  companyGroups: {
    display: 'flex',
    flexDirection: { xs: 'column', lg: 'row' },
    flexWrap: 'wrap',
    gap: { xs: 2, sm: 2.5, md: 3 },
    justifyContent: 'center',
    alignItems: { xs: 'center', lg: 'flex-start' },
    maxWidth: '100%',
  },
  
  // Group Item Styles
  groupContainer: {
    position: 'relative',
    mb: { xs: 2, sm: 3, md: 4 },
    width: { xs: '100%', lg: 'auto' },
    maxWidth: { xs: '100%', lg: 'none' },
  },
  
  groupExpanded: {
    pt: { xs: 1, sm: 2, md: 3 },
    position: 'relative',
    px: { xs: 0, sm: 0 },
  },
  
  groupTeams: {
    display: 'flex',
    flexDirection: { xs: 'column', xl: 'row' },
    flexWrap: 'wrap',
    gap: { xs: 1.5, sm: 2, md: 3 },
    justifyContent: 'center',
    alignItems: { xs: 'center', xl: 'flex-start' },
    pt: { xs: 1, sm: 2, md: 3 },
  },
  
  // Team Item Styles
  teamContainer: {
    position: 'relative',
    mb: { xs: 1.5, sm: 2, md: 3 },
    width: { xs: '100%', xl: 'auto' },
    maxWidth: { xs: '100%', xl: 'none' },
  },
  
  teamExpanded: {
    pt: { xs: 1, sm: 1.5, md: 2 },
    position: 'relative',
    px: { xs: 0, sm: 0 },
  },
  
  teamMembers: {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(auto-fit, minmax(140px, 1fr))',
      sm: 'repeat(auto-fit, minmax(160px, 1fr))',
      md: 'repeat(auto-fit, minmax(180px, 1fr))',
    },
    gap: { xs: 1, sm: 1.5, md: 2 },
    justifyItems: 'center',
    pt: { xs: 1, sm: 1.5, md: 2 },
    maxWidth: { xs: '100%', lg: '700px' },
    mx: 'auto',
  },
  
  // Member Item Styles
  memberContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: { xs: '100%', sm: 'none' },
  },
  
  memberCard: {
    width: '100%',
    minWidth: { xs: 140, sm: 160, md: 180 },
    maxWidth: { xs: 150, sm: 170, md: 190 },
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: { xs: 1, sm: 1.5 },
    p: { xs: 1, sm: 1.5 },
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'default',
    '&:hover': {
      boxShadow: 2,
      borderColor: 'primary.light',
    },
  },
  
  memberContent: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'center', sm: 'flex-start' },
    gap: { xs: 0.5, sm: 1 },
    textAlign: { xs: 'center', sm: 'left' },
  },
  
  memberIcon: {
    width: { xs: 32, sm: 30, md: 32 },
    height: { xs: 32, sm: 30, md: 32 },
    borderRadius: 1.5,
    bgcolor: 'primary.light',
    opacity: 0.15,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    border: '1px solid',
    borderColor: 'primary.light',
    opacity: 0.3,
  },
  
  memberText: {
    flex: 1,
    minWidth: 0,
    width: { xs: '100%', sm: 'auto' },
  },
  
  memberName: {
    lineHeight: 1.2,
    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
    fontWeight: 600,
  },
  
  memberTitle: {
    display: 'block',
    lineHeight: 1.2,
    mt: 0.25,
    fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
  },
  
  memberChip: {
    mt: { xs: 0.5, sm: 0.25 },
    fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' },
    height: { xs: 16, sm: 18, md: 20 },
    '& .MuiChip-label': {
      px: { xs: 0.5, sm: 0.75 },
    },
  },
  
  // Unassigned Users Section - Enhanced design
  unassignedContainer: {
    width: '100%',
    maxWidth: '100%',
    padding: { xs: 2, sm: 3, md: 4 },
    bgcolor: (theme) => theme.palette.mode === 'dark' 
      ? 'rgba(158, 158, 158, 0.03)' 
      : 'rgba(158, 158, 158, 0.02)',
    borderRadius: { xs: 2, sm: 3 },
    border: '1px solid',
    borderColor: (theme) => theme.palette.mode === 'dark' 
      ? 'rgba(158, 158, 158, 0.1)' 
      : 'rgba(158, 158, 158, 0.15)',
    backdropFilter: 'blur(10px)',
    position: 'relative',
    '&::before': {
      content: '"Unassigned Users"',
      position: 'absolute',
      top: -10,
      left: 20,
      bgcolor: 'background.paper',
      px: 2,
      py: 0.5,
      color: 'text.secondary',
      fontSize: '0.75rem',
      fontWeight: 600,
      borderRadius: 1,
      border: '1px solid',
      borderColor: (theme) => theme.palette.mode === 'dark' 
        ? 'rgba(158, 158, 158, 0.2)' 
        : 'rgba(158, 158, 158, 0.3)',
    },
  },
  
  unassignedHeader: {
    display: 'flex',
    justifyContent: 'center',
    mb: { xs: 2, sm: 3, md: 4 },
    px: { xs: 1, sm: 0 },
  },
  
  unassignedExpanded: {
    position: 'relative',
    px: { xs: 0, sm: 1, md: 0 },
  },
  
  unassignedUsers: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    flexWrap: 'wrap',
    gap: { xs: 2, sm: 3, md: 4 },
    justifyContent: 'center',
    alignItems: 'center',
    pt: { xs: 2, sm: 3, md: 4 },
    px: { xs: 1, sm: 2 },
    minHeight: 60,
  },
  

  
  // Modern Tree Connection Lines - Dark mode enhanced
  verticalLine: (height, fromTop = false) => ({
    position: 'absolute',
    width: 3,
    height: height,
    left: '50%',
    transform: 'translateX(-50%)',
    top: fromTop ? -height : '100%',
    background: (theme) => theme.palette.mode === 'dark'
      ? 'linear-gradient(to bottom, rgba(255,255,255,0.3), rgba(255,255,255,0.1))'
      : 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.1))',
    borderRadius: 2,
    opacity: 0.8,
    zIndex: 0,
    display: { xs: 'none', lg: 'block' },
    boxShadow: (theme) => theme.palette.mode === 'dark'
      ? '0 1px 3px rgba(255,255,255,0.1)'
      : '0 1px 3px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease-in-out',
    '&::before': {
      content: '""',
      position: 'absolute',
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: 'primary.main',
      border: '2px solid',
      borderColor: 'background.paper',
      left: '50%',
      transform: 'translateX(-50%)',
      top: fromTop ? '100%' : -6,
      opacity: 0.9,
      boxShadow: (theme) => theme.palette.mode === 'dark'
        ? '0 0 0 2px rgba(25, 118, 210, 0.3)'
        : '0 0 0 2px rgba(25, 118, 210, 0.2)',
      transition: 'all 0.3s ease-in-out',
    }
  }),
  
  horizontalLine: (left, right, top) => ({
    position: 'absolute',
    height: 3,
    left: left,
    right: right,
    top: top,
    background: (theme) => theme.palette.mode === 'dark'
      ? 'linear-gradient(to right, rgba(255,255,255,0.1), rgba(255,255,255,0.3), rgba(255,255,255,0.1))'
      : 'linear-gradient(to right, rgba(0,0,0,0.1), rgba(0,0,0,0.3), rgba(0,0,0,0.1))',
    borderRadius: 2,
    opacity: 0.8,
    zIndex: 0,
    display: { xs: 'none', lg: 'block' },
    boxShadow: (theme) => theme.palette.mode === 'dark'
      ? '0 1px 3px rgba(255,255,255,0.1)'
      : '0 1px 3px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease-in-out',
  }),
  
  // Tree connector for members - Dark mode enhanced
  memberConnector: {
    position: 'absolute',
    width: 2,
    height: 20,
    left: '50%',
    transform: 'translateX(-50%)',
    top: -20,
    background: (theme) => theme.palette.mode === 'dark'
      ? 'linear-gradient(to bottom, rgba(255,255,255,0.3), rgba(255,255,255,0.1))'
      : 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.1))',
    borderRadius: 1,
    opacity: 0.7,
    zIndex: 0,
    display: { xs: 'none', lg: 'block' },
    boxShadow: (theme) => theme.palette.mode === 'dark'
      ? '0 1px 2px rgba(255,255,255,0.1)'
      : '0 1px 2px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease-in-out',
    '&::after': {
      content: '""',
      position: 'absolute',
      width: 10,
      height: 2,
      background: (theme) => theme.palette.mode === 'dark'
        ? 'linear-gradient(to right, rgba(255,255,255,0.1), rgba(255,255,255,0.3), rgba(255,255,255,0.1))'
        : 'linear-gradient(to right, rgba(0,0,0,0.1), rgba(0,0,0,0.3), rgba(0,0,0,0.1))',
      borderRadius: 1,
      left: '50%',
      transform: 'translateX(-50%)',
      bottom: 0,
      opacity: 0.7,
      boxShadow: (theme) => theme.palette.mode === 'dark'
        ? '0 1px 2px rgba(255,255,255,0.1)'
        : '0 1px 2px rgba(0,0,0,0.1)',
    }
  },
  
  // Company expansion lines - Dark mode enhanced
  companyLines: {
    verticalDown: {
      position: 'absolute',
      width: 3,
      height: 30,
      left: '50%',
      transform: 'translateX(-50%)',
      top: '100%',
      background: (theme) => theme.palette.mode === 'dark'
        ? `linear-gradient(to bottom, ${theme.palette.warning.main}, rgba(255,255,255,0.1))`
        : `linear-gradient(to bottom, ${theme.palette.warning.main}, rgba(0,0,0,0.1))`,
      opacity: 0.8,
      zIndex: 0,
      display: { xs: 'none', lg: 'block' },
      borderRadius: 2,
      boxShadow: (theme) => theme.palette.mode === 'dark'
        ? '0 2px 4px rgba(255,255,255,0.1)'
        : '0 2px 4px rgba(0,0,0,0.1)',
      transition: 'all 0.3s ease-in-out',
    },
    horizontalSpread: {
      position: 'absolute',
      height: 3,
      left: '15%',
      right: '15%',
      top: 30,
      background: (theme) => theme.palette.mode === 'dark'
        ? `linear-gradient(to right, rgba(255,255,255,0.1), ${theme.palette.warning.main}, rgba(255,255,255,0.1))`
        : `linear-gradient(to right, rgba(0,0,0,0.1), ${theme.palette.warning.main}, rgba(0,0,0,0.1))`,
      opacity: 0.8,
      zIndex: 0,
      display: { xs: 'none', lg: 'block' },
      borderRadius: 2,
      boxShadow: (theme) => theme.palette.mode === 'dark'
        ? '0 2px 4px rgba(255,255,255,0.1)'
        : '0 2px 4px rgba(0,0,0,0.1)',
      transition: 'all 0.3s ease-in-out',
    }
  },
  
  // Group expansion lines - Dark mode enhanced
  groupLines: {
    verticalDown: {
      position: 'absolute',
      width: 3,
      height: 25,
      left: '50%',
      transform: 'translateX(-50%)',
      top: '100%',
      background: (theme) => theme.palette.mode === 'dark'
        ? `linear-gradient(to bottom, ${theme.palette.info.main}, rgba(255,255,255,0.1))`
        : `linear-gradient(to bottom, ${theme.palette.info.main}, rgba(0,0,0,0.1))`,
      opacity: 0.7,
      zIndex: 0,
      display: { xs: 'none', lg: 'block' },
      borderRadius: 2,
      boxShadow: (theme) => theme.palette.mode === 'dark'
        ? '0 2px 4px rgba(255,255,255,0.1)'
        : '0 2px 4px rgba(0,0,0,0.1)',
      transition: 'all 0.3s ease-in-out',
    },
    horizontalSpread: {
      position: 'absolute',
      height: 3,
      left: '20%',
      right: '20%',
      top: 25,
      background: (theme) => theme.palette.mode === 'dark'
        ? `linear-gradient(to right, rgba(255,255,255,0.1), ${theme.palette.info.main}, rgba(255,255,255,0.1))`
        : `linear-gradient(to right, rgba(0,0,0,0.1), ${theme.palette.info.main}, rgba(0,0,0,0.1))`,
      opacity: 0.7,
      zIndex: 0,
      display: { xs: 'none', lg: 'block' },
      borderRadius: 2,
      boxShadow: (theme) => theme.palette.mode === 'dark'
        ? '0 2px 4px rgba(255,255,255,0.1)'
        : '0 2px 4px rgba(0,0,0,0.1)',
      transition: 'all 0.3s ease-in-out',
    }
  },
  
  // Team expansion lines - Dark mode enhanced
  teamLines: {
    verticalDown: {
      position: 'absolute',
      width: 3,
      height: 20,
      left: '50%',
      transform: 'translateX(-50%)',
      top: '100%',
      background: (theme) => theme.palette.mode === 'dark'
        ? `linear-gradient(to bottom, ${theme.palette.success.main}, rgba(255,255,255,0.1))`
        : `linear-gradient(to bottom, ${theme.palette.success.main}, rgba(0,0,0,0.1))`,
      opacity: 0.6,
      zIndex: 0,
      display: { xs: 'none', lg: 'block' },
      borderRadius: 2,
      boxShadow: (theme) => theme.palette.mode === 'dark'
        ? '0 2px 4px rgba(255,255,255,0.1)'
        : '0 2px 4px rgba(0,0,0,0.1)',
      transition: 'all 0.3s ease-in-out',
    },
    horizontalSpread: {
      position: 'absolute',
      height: 3,
      left: '25%',
      right: '25%',
      top: 20,
      background: (theme) => theme.palette.mode === 'dark'
        ? `linear-gradient(to right, rgba(255,255,255,0.1), ${theme.palette.success.main}, rgba(255,255,255,0.1))`
        : `linear-gradient(to right, rgba(0,0,0,0.1), ${theme.palette.success.main}, rgba(0,0,0,0.1))`,
      opacity: 0.6,
      zIndex: 0,
      display: { xs: 'none', lg: 'block' },
      borderRadius: 2,
      boxShadow: (theme) => theme.palette.mode === 'dark'
        ? '0 2px 4px rgba(255,255,255,0.1)'
        : '0 2px 4px rgba(0,0,0,0.1)',
      transition: 'all 0.3s ease-in-out',
    }
  },
  
  // Desktop only utilities
  desktopOnly: {
    display: { xs: 'none', lg: 'block' },
  },
  
  // Enhanced tree animations and effects
  animatedTreeLine: {
    transition: 'all 0.3s ease-in-out',
    '&::before': {
      transition: 'all 0.3s ease-in-out',
    },
    '&::after': {
      transition: 'all 0.3s ease-in-out',
    },
  },
  
  // Tree connection points with glow effect
  connectionPoint: {
    position: 'absolute',
    width: 8,
    height: 8,
    borderRadius: '50%',
    bgcolor: 'primary.main',
    border: '2px solid',
    borderColor: 'background.paper',
    boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
    display: { xs: 'none', lg: 'block' },
    '&::before': {
      content: '""',
      position: 'absolute',
      width: 12,
      height: 12,
      borderRadius: '50%',
      bgcolor: 'primary.main',
      opacity: 0.2,
      top: -2,
      left: -2,
      animation: 'pulse 2s infinite',
    },
    '@keyframes pulse': {
      '0%': {
        transform: 'scale(1)',
        opacity: 0.2,
      },
      '50%': {
        transform: 'scale(1.2)',
        opacity: 0.4,
      },
      '100%': {
        transform: 'scale(1)',
        opacity: 0.2,
      },
    },
  },
}; 